import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, inp, label, card, btnPrimary } from "./AdminShell";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapLink from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  ArrowLeft, Plus, Trash2, Upload, X, Star, Save, ExternalLink, ChevronDown,
  Bold, Italic, Heading2, List, ListOrdered, Link2, Undo2, GripVertical, MoveUp, MoveDown,
} from "lucide-react";

const STOCK_OPTS = ["Tersedia", "Terjual", "Segera Hadir", "Hubungi Kami"];
const COND_OPTS = ["Baru", "Rekondisi", "Bekas", "Hubungi Kami"];
const IMAGE_LABELS = ["", "Foto Utama", "Depan", "Belakang", "Sisi Kiri", "Sisi Kanan", "Engine", "Control Panel", "Hour Meter", "Nameplate", "Chassis", "Detail", "Dokumentasi"];

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const emptyProduct = {
  product_name: "", brand: "", model: "", category: "", subcategory: "", condition: "Hubungi Kami",
  short_description: "", full_description: "", specifications: [],
  stock_status: "Hubungi Kami", featured: false, published: false,
  seo_title: "", seo_description: "", seo_keywords: "",
  featured_image: "", gallery_images: [], slug: "",
  video: { type: "youtube", url: "", title: "", description: "", thumbnail: "", published: false },
  documents: [],
};

const DOC_TYPES = ["Brochure", "Technical Datasheet", "Certificate", "Test Report", "Manual", "Other"];
const VIDEO_TYPES = [
  { v: "youtube", l: "YouTube" },
  { v: "vimeo", l: "Vimeo" },
  { v: "direct", l: "Direct Video (MP4/WebM)" },
];

function parseVideoPreview(type, url) {
  if (!url) return null;
  if (type === "youtube") {
    const m = String(url).match(/(?:youtube\.com\/watch\?(?:.*&)?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([\w-]{11})/i);
    if (m) return { thumb: `https://i.ytimg.com/vi/${m[1]}/hqdefault.jpg`, ok: true, id: m[1], provider: "YouTube" };
    return { ok: false };
  }
  if (type === "vimeo") {
    const m = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (m) return { ok: true, id: m[1], provider: "Vimeo" };
    return { ok: false };
  }
  return { ok: /^https?:\/\//i.test(url) };
}

/* ============ SECTION ============ */
function Section({ title, subtitle, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`${card} overflow-hidden`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-neutral-50 transition text-left">
        <div>
          <h2 className="font-display font-bold text-neutral-900">{title}</h2>
          {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
        </div>
        <ChevronDown size={18} className={`text-neutral-400 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {/* Keep children mounted so form inputs are always testable; toggle visibility only. */}
      <div className={`px-5 pb-5 border-t border-neutral-100 pt-4 ${open ? "" : "hidden"}`}>{children}</div>
    </div>
  );
}

/* ============ VIDEO SECTION ============ */
function ProductVideoSection({ product, set }) {
  const v = product.video || { type: "youtube", url: "", title: "", description: "", thumbnail: "", published: false };
  const setV = (k, val) => set("video", { ...v, [k]: val });
  const preview = parseVideoPreview(v.type, v.url);
  return (
    <Section title="Video Produk" subtitle="YouTube, Vimeo, atau file MP4 langsung (opsional)" defaultOpen={true}>
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={label}>Tipe Video</label>
            <select value={v.type} onChange={(e) => setV("type", e.target.value)} className={inp} data-testid="pf-video-type">
              {VIDEO_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className={label}>URL Video *</label>
            <input value={v.url} onChange={(e) => setV("url", e.target.value)} className={inp} data-testid="pf-video-url"
              placeholder={v.type === "youtube" ? "https://youtube.com/watch?v=..." : v.type === "vimeo" ? "https://vimeo.com/..." : "https://.../video.mp4"} />
            {v.url && preview && !preview.ok && <p className="text-[11px] text-red-600 mt-1">URL tidak valid untuk tipe {v.type}.</p>}
            {v.url && preview?.ok && <p className="text-[11px] text-green-600 mt-1">✓ URL valid {preview.provider && `(${preview.provider})`}</p>}
          </div>
        </div>
        <div><label className={label}>Judul Video</label><input value={v.title || ""} onChange={(e) => setV("title", e.target.value)} className={inp} data-testid="pf-video-title" placeholder="Demonstrasi Akurad Cairan Anti Api AF21" /></div>
        <div><label className={label}>Deskripsi Video</label><textarea rows={2} value={v.description || ""} onChange={(e) => setV("description", e.target.value)} className={inp} /></div>
        <div>
          <label className={label}>URL Thumbnail / Poster (opsional)</label>
          <input value={v.thumbnail || ""} onChange={(e) => setV("thumbnail", e.target.value)} className={inp} placeholder="Kosongkan untuk gunakan thumbnail otomatis (YouTube)" />
          {(v.thumbnail || preview?.thumb) && (
            <img src={v.thumbnail || preview.thumb} alt="poster preview" className="mt-2 w-full max-w-sm aspect-video object-cover rounded-md border border-neutral-200" />
          )}
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!v.published} onChange={(e) => setV("published", e.target.checked)} className="accent-[#FF9418] w-4 h-4" data-testid="pf-video-published" />
          <span className="text-sm font-semibold text-neutral-900">Tampilkan video di halaman produk</span>
        </label>
        <p className="text-[11px] text-neutral-500">Kalau tidak dicentang atau URL kosong, section video tidak akan muncul di frontend.</p>
      </div>
    </Section>
  );
}

/* ============ DOCUMENTS SECTION ============ */
function ProductDocumentsSection({ product, set, uploadFile }) {
  const docs = product.documents || [];
  const [uploading, setUploading] = useState(false);
  const addDoc = () => set("documents", [...docs, { id: crypto.randomUUID?.() || String(Date.now() + Math.random()), title: "", doc_type: "Brochure", url: "", storage_path: "" }]);
  const updDoc = (i, k, v) => { const arr = [...docs]; arr[i] = { ...arr[i], [k]: v }; set("documents", arr); };
  const rmDoc = (i) => set("documents", docs.filter((_, j) => j !== i));
  const moveDoc = (i, dir) => {
    const arr = [...docs]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; set("documents", arr);
  };
  const uploadDoc = async (i, file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Ukuran maksimum 25 MB."); return; }
    if (!["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Format tidak didukung. Gunakan PDF, JPEG, PNG, atau WebP."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const arr = [...docs];
      arr[i] = { ...arr[i], url: fileUrl(data.path), storage_path: data.path };
      if (!arr[i].title) arr[i].title = (file.name || "").replace(/\.[^.]+$/, "");
      set("documents", arr);
      toast.success("Dokumen berhasil diunggah.");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal upload dokumen"); }
    finally { setUploading(false); }
  };
  return (
    <Section title="Dokumen Produk" subtitle="Brosur, datasheet, sertifikat, test report, manual (PDF/JPEG/PNG, maks 25 MB)" defaultOpen={true}>
      <div className="space-y-2">
        {docs.map((d, i) => (
          <div key={d.id || i} className="border border-neutral-200 rounded-md p-3 bg-neutral-50 space-y-2" data-testid={`doc-row-${i}`}>
            <div className="flex items-start gap-2">
              <div className="flex flex-col pt-2">
                <button type="button" onClick={() => moveDoc(i, -1)} className="text-neutral-400 hover:text-[#FF9418] disabled:opacity-30" disabled={i === 0}><MoveUp size={12} /></button>
                <button type="button" onClick={() => moveDoc(i, 1)} className="text-neutral-400 hover:text-[#FF9418] disabled:opacity-30" disabled={i === docs.length - 1}><MoveDown size={12} /></button>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                <input value={d.title} onChange={(e) => updDoc(i, "title", e.target.value)} placeholder="Judul (mis: Test Report Fire Resistance)" className={inp} data-testid={`doc-title-${i}`} />
                <select value={d.doc_type || "Brochure"} onChange={(e) => updDoc(i, "doc_type", e.target.value)} className={inp}>
                  {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <div className="flex gap-1">
                  <label className={`${inp} cursor-pointer flex items-center justify-center gap-1 text-xs`}>
                    <Upload size={13} /> {uploading ? "..." : "Upload"}
                    <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadDoc(i, e.target.files[0])} data-testid={`doc-upload-${i}`} />
                  </label>
                  <button type="button" onClick={() => rmDoc(i)} className="text-red-500 hover:text-red-700 p-2"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
            {d.url ? (
              <div className="text-[11px] text-neutral-600 pl-6 flex items-center gap-2">
                <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-mono">✓ Terupload</span>
                <a href={d.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${d.url}` : d.url} target="_blank" rel="noreferrer" className="text-[#FF9418] hover:underline truncate">Preview</a>
              </div>
            ) : (
              <div className="pl-6">
                <input value={d.url || ""} onChange={(e) => updDoc(i, "url", e.target.value)} placeholder="atau tempel URL dokumen" className={`${inp} text-xs`} />
              </div>
            )}
          </div>
        ))}
        {docs.length === 0 && <div className="text-sm text-neutral-500 py-2">Belum ada dokumen. Klik "Tambah Dokumen" untuk memulai.</div>}
        <Button type="button" onClick={addDoc} variant="outline" className="border-neutral-300 mt-2 h-9" data-testid="add-doc"><Plus size={13} /> Tambah Dokumen</Button>
      </div>
    </Section>
  );
}

/* ============ RICH TEXT ============ */
function RichText({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      TiptapLink.configure({ openOnClick: false, HTMLAttributes: { class: "text-[#FF9418] underline" } }),
      Placeholder.configure({ placeholder: "Tulis deskripsi lengkap produk di sini..." }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "prose prose-sm max-w-none min-h-[180px] px-3 py-2 focus:outline-none" } },
  });
  useEffect(() => {
    if (editor && value !== editor.getHTML() && value !== undefined) {
      editor.commands.setContent(value || "", false);
    }
  // eslint-disable-next-line
  }, [value === "" && editor]);

  if (!editor) return null;

  const Btn = ({ onClick, active, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`p-1.5 rounded ${active ? "bg-[#FF9418]/10 text-[#FF9418]" : "text-neutral-600 hover:bg-neutral-100"}`}>
      {children}
    </button>
  );

  return (
    <div className="border border-neutral-300 rounded-md bg-white focus-within:border-[#FF9418] focus-within:ring-2 focus-within:ring-[#FF9418]/20">
      <div className="flex flex-wrap gap-0.5 px-2 py-1.5 border-b border-neutral-200 bg-neutral-50 rounded-t-md">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} testid="rt-bold"><Bold size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} testid="rt-italic"><Italic size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} testid="rt-h2"><Heading2 size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}><List size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}><ListOrdered size={14} /></Btn>
        <Btn onClick={() => {
          const url = window.prompt("URL:", editor.getAttributes("link").href || "https://");
          if (url === null) return;
          if (url === "") editor.chain().focus().unsetLink().run();
          else editor.chain().focus().setLink({ href: url }).run();
        }} active={editor.isActive("link")}><Link2 size={14} /></Btn>
        <div className="w-px bg-neutral-300 mx-1 my-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()}><Undo2 size={14} /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

/* ============ MAIN FORM ============ */
export function AdminProductForm() {
  const { id } = useParams();
  const isEdit = id && id !== "new";
  const nav = useNavigate();
  const [p, setP] = useState(emptyProduct);
  const [initial, setInitial] = useState(emptyProduct);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    if (isEdit) {
      api.get(`/admin/products/${id}`).then((r) => {
        const data = { ...emptyProduct, ...r.data };
        setP(data); setInitial(data); setSlugTouched(true);
      }).catch(() => toast.error("Gagal memuat produk")).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  // Load Admin categories once so the Kategori field is a controlled dropdown
  // sourced from Admin > Categories (single source of truth).
  useEffect(() => {
    api.get("/admin/categories")
      .then((r) => setCategoryOptions((r.data || []).filter((c) => c.published !== false)))
      .catch(() => setCategoryOptions([]));
  }, []);

  const dirty = useMemo(() => JSON.stringify(p) !== JSON.stringify(initial), [p, initial]);

  // Warn on page unload
  useEffect(() => {
    const beforeUnload = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  const set = (k, v) => setP((s) => {
    const next = { ...s, [k]: v };
    if (k === "product_name" && !slugTouched) next.slug = slugify(v);
    return next;
  });

  // Specs
  const addSpec = () => set("specifications", [...(p.specifications || []), { name: "", value: "" }]);
  const updSpec = (i, k, v) => { const arr = [...p.specifications]; arr[i] = { ...arr[i], [k]: v }; set("specifications", arr); };
  const rmSpec = (i) => set("specifications", p.specifications.filter((_, j) => j !== i));
  const moveSpec = (i, dir) => {
    const arr = [...p.specifications]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; set("specifications", arr);
  };

  // Uploads
  const uploadFile = async (file) => {
    if (file.size > 10 * 1024 * 1024) { toast.error("Ukuran maksimum 10 MB."); return null; }
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) { toast.error("Format tidak didukung. Gunakan JPEG, PNG, atau WebP."); return null; }
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { path: data.path, url: fileUrl(data.path) };
  };

  const uploadFeatured = async (file) => {
    const r = await uploadFile(file);
    if (r) { set("featured_image", r.url); toast.success("Foto utama berhasil diunggah."); }
  };

  const uploadGallery = async (files) => {
    const list = [...(p.gallery_images || [])];
    let ok = 0;
    for (const file of Array.from(files)) {
      const r = await uploadFile(file);
      if (r) { list.push({ id: crypto.randomUUID?.() || String(Date.now() + Math.random()), url: r.url, storage_path: r.path, alt: "", label: "" }); ok++; }
    }
    set("gallery_images", list);
    if (ok) toast.success(`${ok} foto berhasil diunggah.`);
  };

  const removeGallery = (i) => set("gallery_images", p.gallery_images.filter((_, j) => j !== i));
  const moveGallery = (i, dir) => {
    const arr = [...p.gallery_images]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; set("gallery_images", arr);
  };
  const setAsFeatured = (i) => {
    const g = p.gallery_images[i]; if (!g) return;
    set("featured_image", g.url); toast.success("Foto utama diperbarui.");
  };
  const updGalleryField = (i, k, v) => { const arr = [...p.gallery_images]; arr[i] = { ...arr[i], [k]: v }; set("gallery_images", arr); };

  const save = async (opts = {}) => {
    if (!p.product_name || !p.brand || !p.model || !p.category) {
      toast.error("Wajib diisi: Nama Produk, Brand, Model, Kategori");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...p, slug: p.slug || slugify(p.product_name) };
      if (opts.publish !== undefined) payload.published = opts.publish;
      const { data } = isEdit
        ? await api.put(`/admin/products/${id}`, payload)
        : await api.post("/admin/products", payload);
      toast.success(isEdit ? "Produk berhasil diperbarui." : "Produk berhasil dibuat.");
      const saved = { ...emptyProduct, ...data };
      setP(saved); setInitial(saved);
      if (!isEdit) nav(`/admin/products/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan produk");
    } finally { setSaving(false); }
  };

  const openPreview = () => {
    const slug = p.slug || slugify(p.product_name);
    if (!slug) return toast.error("Isi nama produk terlebih dulu.");
    window.open(`/produk/${slug}${!p.published ? "?preview=1" : ""}`, "_blank");
  };

  const goBack = () => {
    if (dirty && !window.confirm("Ada perubahan yang belum disimpan. Yakin ingin keluar?")) return;
    nav("/admin/products");
  };

  if (loading) return <div className="text-neutral-500">Memuat...</div>;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-sm">
        <button onClick={goBack} className="text-neutral-500 hover:text-[#FF9418] flex items-center gap-1"><ArrowLeft size={14} /> Kembali ke Products</button>
      </div>
      <PageHeader
        title={isEdit ? "Edit Produk" : "Produk Baru"}
        subtitle={dirty ? "Ada perubahan yang belum disimpan" : (isEdit ? "Kelola detail produk" : "Buat produk baru")}
        actions={<>
          {isEdit && <Button onClick={openPreview} type="button" variant="outline" className="h-10 border-neutral-300"><ExternalLink size={14} /> Preview</Button>}
          <Button onClick={() => save({ publish: false })} type="button" variant="outline" className="h-10 border-neutral-300" disabled={saving} data-testid="save-draft">Simpan Draft</Button>
          <Button onClick={() => save({ publish: true })} type="button" className={`${btnPrimary} h-10 px-5`} disabled={saving} data-testid="save-publish">
            <Save size={14} /> {saving ? "Menyimpan..." : (p.published ? "Update & Publish" : "Publish")}
          </Button>
        </>} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* A. Basic Info */}
          <Section title="Informasi Dasar" subtitle="Nama, brand, model, dan kategori" defaultOpen>
            <div className="space-y-3">
              <div><label className={label}>Nama Produk *</label><input value={p.product_name} onChange={(e) => set("product_name", e.target.value)} className={inp} data-testid="pf-name" placeholder="Contoh: Airman PDS 175S" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Brand *</label><input value={p.brand} onChange={(e) => set("brand", e.target.value)} className={inp} data-testid="pf-brand" /></div>
                <div><label className={label}>Model *</label><input value={p.model} onChange={(e) => set("model", e.target.value)} className={inp} data-testid="pf-model" /></div>
                <div>
                  <label className={label}>Kategori *</label>
                  <select value={p.category || ""} onChange={(e) => set("category", e.target.value)} className={inp} data-testid="pf-category">
                    <option value="">— Pilih Kategori —</option>
                    {categoryOptions.map((c) => (
                      <option key={c.id || c.slug} value={c.name}>{c.name}</option>
                    ))}
                    {p.category && !categoryOptions.some((c) => c.name === p.category) && (
                      <option value={p.category}>{p.category} (legacy)</option>
                    )}
                  </select>
                  <p className="text-[11px] text-neutral-500 mt-1">Kategori diambil dari Admin &gt; Categories. Tambah/ubah kategori di sana.</p>
                </div>
                <div><label className={label}>Sub Kategori</label><input value={p.subcategory} onChange={(e) => set("subcategory", e.target.value)} className={inp} data-testid="pf-subcategory" placeholder="Opsional" /></div>
              </div>
              <div>
                <label className={label}>URL Slug</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-500 font-mono whitespace-nowrap">/produk/</span>
                  <input value={p.slug || ""} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} className={inp} data-testid="pf-slug" />
                </div>
                <p className="text-[11px] text-neutral-500 mt-1">Auto-generated dari nama produk. Slug harus unik.</p>
              </div>
            </div>
          </Section>

          {/* C. Description */}
          <Section title="Deskripsi Produk" subtitle="Ringkasan pendek & konten lengkap">
            <div className="space-y-3">
              <div><label className={label}>Deskripsi Singkat</label><textarea rows={2} value={p.short_description} onChange={(e) => set("short_description", e.target.value)} className={inp} placeholder="Ringkasan 1-2 kalimat untuk product card & meta description" data-testid="pf-short" /></div>
              <div><label className={label}>Deskripsi Lengkap</label><RichText value={p.full_description} onChange={(v) => set("full_description", v)} /></div>
            </div>
          </Section>

          {/* D. Photos */}
          <Section title="Foto Produk" subtitle="Foto utama & gallery">
            <div className="space-y-4">
              <div>
                <label className={label}>Foto Utama (Featured Image)</label>
                {p.featured_image ? (
                  <div className="relative w-full max-w-md aspect-video bg-neutral-100 rounded-md overflow-hidden mb-2 group">
                    <img src={p.featured_image} alt="featured" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => set("featured_image", "")} className="absolute top-2 right-2 bg-white/90 text-red-600 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"><X size={16} /></button>
                  </div>
                ) : null}
                <label className="border-2 border-dashed border-neutral-300 hover:border-[#FF9418] p-6 rounded-md text-center cursor-pointer block max-w-md transition">
                  <Upload size={20} className="mx-auto mb-2 text-neutral-400" />
                  <div className="text-sm text-neutral-700 font-semibold">Klik untuk upload foto utama</div>
                  <div className="text-[11px] text-neutral-500 mt-1">JPEG, PNG, atau WebP · maksimum 10 MB · rekomendasi 1600×1200</div>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFeatured(e.target.files[0])} data-testid="pf-upload-featured" />
                </label>
                <input value={p.featured_image} onChange={(e) => set("featured_image", e.target.value)} placeholder="atau tempel URL" className={`${inp} mt-2 text-xs`} />
              </div>

              <div>
                <label className={label}>Gallery ({(p.gallery_images || []).length} foto)</label>
                {(p.gallery_images || []).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                    {p.gallery_images.map((g, i) => (
                      <div key={g.id || i} className="border border-neutral-200 rounded-md overflow-hidden bg-white">
                        <div className="aspect-square bg-neutral-100 relative group">
                          <img src={g.url} alt={g.alt || ""} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
                            <button type="button" onClick={() => setAsFeatured(i)} title="Set sebagai foto utama" className="bg-white/90 text-[#FF9418] w-8 h-8 rounded flex items-center justify-center"><Star size={14} /></button>
                            <button type="button" onClick={() => moveGallery(i, -1)} disabled={i === 0} className="bg-white/90 text-neutral-700 w-8 h-8 rounded flex items-center justify-center disabled:opacity-30"><MoveUp size={14} /></button>
                            <button type="button" onClick={() => moveGallery(i, 1)} disabled={i === p.gallery_images.length - 1} className="bg-white/90 text-neutral-700 w-8 h-8 rounded flex items-center justify-center disabled:opacity-30"><MoveDown size={14} /></button>
                            <button type="button" onClick={() => removeGallery(i)} className="bg-white/90 text-red-600 w-8 h-8 rounded flex items-center justify-center"><Trash2 size={14} /></button>
                          </div>
                        </div>
                        <div className="p-2 space-y-1">
                          <select value={g.label || ""} onChange={(e) => updGalleryField(i, "label", e.target.value)} className="w-full text-[11px] bg-neutral-50 border border-neutral-200 rounded px-2 py-1 outline-none">
                            {IMAGE_LABELS.map((l) => <option key={l} value={l}>{l || "— Label —"}</option>)}
                          </select>
                          <input value={g.alt || ""} onChange={(e) => updGalleryField(i, "alt", e.target.value)} placeholder="Alt text (SEO)" className="w-full text-[11px] bg-white border border-neutral-200 rounded px-2 py-1 outline-none" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <label className="border-2 border-dashed border-neutral-300 hover:border-[#FF9418] p-4 rounded-md text-center cursor-pointer block transition">
                  <Upload size={16} className="mx-auto mb-1 text-neutral-400" />
                  <div className="text-xs text-neutral-700">Klik untuk upload multiple foto</div>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files?.length && uploadGallery(e.target.files)} data-testid="pf-upload-gallery" />
                </label>
              </div>
            </div>
          </Section>

          {/* E. Specifications */}
          <Section title="Spesifikasi Teknis" subtitle="Tambahkan baris dinamis Nama : Nilai (jangan invent data yang tidak diketahui)">
            <div className="space-y-2">
              {(p.specifications || []).map((sp, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button type="button" onClick={() => moveSpec(i, -1)} className="text-neutral-400 hover:text-[#FF9418] disabled:opacity-30" disabled={i === 0}><MoveUp size={12} /></button>
                    <button type="button" onClick={() => moveSpec(i, 1)} className="text-neutral-400 hover:text-[#FF9418] disabled:opacity-30" disabled={i === p.specifications.length - 1}><MoveDown size={12} /></button>
                  </div>
                  <input value={sp.name} onChange={(e) => updSpec(i, "name", e.target.value)} placeholder="Nama (mis: Working Pressure)" className={`${inp} flex-1`} />
                  <input value={sp.value} onChange={(e) => updSpec(i, "value", e.target.value)} placeholder="Nilai (mis: 7 bar)" className={`${inp} flex-1`} />
                  <button type="button" onClick={() => rmSpec(i)} className="text-red-500 hover:text-red-700 p-2"><Trash2 size={14} /></button>
                </div>
              ))}
              {(p.specifications || []).length === 0 && <div className="text-sm text-neutral-500 py-2">Belum ada spesifikasi. Klik "Tambah Baris" untuk memulai.</div>}
              <Button type="button" onClick={addSpec} variant="outline" className="border-neutral-300 mt-2 h-9" data-testid="add-spec"><Plus size={13} /> Tambah Baris Spesifikasi</Button>
            </div>
          </Section>

          {/* Video */}
          <ProductVideoSection product={p} set={set} />

          {/* Documents */}
          <ProductDocumentsSection product={p} set={set} uploadFile={uploadFile} />

          {/* F. SEO */}
          <Section title="SEO" subtitle="Metadata untuk mesin pencari & social sharing" defaultOpen={false}>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1"><label className={label}>SEO Title</label><span className="text-[10px] text-neutral-400 font-mono">{(p.seo_title || "").length}/60</span></div>
                <input value={p.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={inp} placeholder={`${p.product_name} | Teknik Diesel Indonesia`} data-testid="pf-seo-title" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1"><label className={label}>Meta Description</label><span className="text-[10px] text-neutral-400 font-mono">{(p.seo_description || "").length}/160</span></div>
                <textarea rows={3} value={p.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={inp} placeholder={p.short_description} data-testid="pf-seo-desc" />
              </div>
              <div><label className={label}>SEO Keywords</label><input value={p.seo_keywords || ""} onChange={(e) => set("seo_keywords", e.target.value)} className={inp} placeholder="dipisah koma" /></div>
              <p className="text-[11px] text-neutral-500">Jika dikosongkan, sistem akan otomatis pakai: title = "{p.product_name || "Nama Produk"} | Teknik Diesel Indonesia", description = deskripsi singkat, dan featured image sebagai OG image.</p>
            </div>
          </Section>
        </div>

        {/* Sidebar: Sales Status + Publish */}
        <div className="space-y-4">
          <div className={`${card} p-5 sticky top-4`}>
            <h2 className="font-display font-bold text-neutral-900 mb-3">Status Publish</h2>
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={p.published} onChange={(e) => set("published", e.target.checked)} className="accent-[#FF9418] w-4 h-4" data-testid="pf-published" />
                <span className="text-sm font-semibold text-neutral-900">Publikasikan ke website</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={p.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#FF9418] w-4 h-4" data-testid="pf-featured" />
                <span className="text-sm text-neutral-700">Produk Unggulan (Featured)</span>
              </label>
            </div>
          </div>

          <div className={`${card} p-5`}>
            <h2 className="font-display font-bold text-neutral-900 mb-3">Status Penjualan</h2>
            <div className="space-y-3">
              <div>
                <label className={label}>Ketersediaan</label>
                <select value={p.stock_status} onChange={(e) => set("stock_status", e.target.value)} className={inp} data-testid="pf-stock">
                  {STOCK_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Kondisi</label>
                <select value={p.condition || "Hubungi Kami"} onChange={(e) => set("condition", e.target.value)} className={inp} data-testid="pf-condition">
                  {COND_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="text-[11px] text-neutral-500 leading-relaxed px-1">
            <strong>Tips akurasi:</strong> Jangan pernah mengisi spesifikasi teknis yang belum dikonfirmasi.
            Kosongkan field yang belum diketahui, sistem akan menampilkan pesan "Hubungi kami" di halaman publik.
          </div>
        </div>
      </div>
    </div>
  );
}
